import React, { useEffect, useState } from "react";
import { Button, CardContent, Grid, Skeleton, Stack, Typography } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import httpClient, { extractApiError } from "../api/httpClient";
import { PageHeader, PagePanel, PageShell, pageTypography } from "../components/PageLayout";
import { useUI } from "../contexts/UIContext";

const fallbackStatus = {
  plan: "FREE",
  status: "INATTIVO",
  renewalDate: "-",
  amount: 0,
  currency: "EUR",
};

export default function Pagamenti() {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(fallbackStatus);
  const [redirecting, setRedirecting] = useState(false);
  const { showNotification } = useUI();

  const loadBilling = async () => {
    setLoading(true);
    try {
      const response = await httpClient.get("/pagamento/abbonamento");
      const data = response?.data || {};
      setSubscription({
        ...fallbackStatus,
        plan: data?.azienda?.piano || data.piano || data.plan || fallbackStatus.plan,
        status: data.stato || data.status || fallbackStatus.status,
        renewalDate: data.dataScadenza || data.renewalDate || fallbackStatus.renewalDate,
        amount: data.importo || data.amount || fallbackStatus.amount,
        currency: data.valuta || data.currency || fallbackStatus.currency,
      });
    } catch (error) {
      // L'endpoint di dettaglio abbonamento puo non essere esposto in alcuni ambienti.
      setSubscription(fallbackStatus);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBilling();
  }, [showNotification]);

  const checkout = async () => {
    setRedirecting(true);
    try {
      const priceId = process.env.REACT_APP_STRIPE_PRICE_ID;
      if (!priceId) {
        throw new Error("REACT_APP_STRIPE_PRICE_ID non configurata");
      }

      const response = await httpClient.post("/pagamento/checkout", {
        priceId,
        successUrl: `${window.location.origin}/pagamenti`,
        cancelUrl: `${window.location.origin}/pagamenti`,
      });
      const checkoutUrl = response?.data?.checkoutUrl || response?.data?.url;

      if (!checkoutUrl) {
        throw new Error("URL checkout Stripe non disponibile");
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      showNotification(extractApiError(error), "error");
      setRedirecting(false);
    }
  };

  return (
    <PageShell>
      <PageHeader
        title="Billing e pagamenti"
        subtitle="Panoramica dell'abbonamento e accesso rapido al checkout con la stessa grammatica visiva della pianificazione."
      />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <PagePanel tone="cool">
            <CardContent>
              <Typography sx={pageTypography.panelLabel}>Piano</Typography>
              <Typography sx={{ ...pageTypography.panelValue, mt: 1 }}>
                {loading ? <Skeleton width={90} /> : subscription.plan}
              </Typography>
            </CardContent>
          </PagePanel>
        </Grid>
        <Grid item xs={12} md={4}>
          <PagePanel tone="warm">
            <CardContent>
              <Typography sx={pageTypography.panelLabel}>Stato</Typography>
              <Typography sx={{ ...pageTypography.panelValue, mt: 1 }}>
                {loading ? <Skeleton width={120} /> : subscription.status}
              </Typography>
            </CardContent>
          </PagePanel>
        </Grid>
        <Grid item xs={12} md={4}>
          <PagePanel tone="neutral">
            <CardContent>
              <Typography sx={pageTypography.panelLabel}>Rinnovo</Typography>
              <Typography sx={{ ...pageTypography.panelValue, mt: 1 }}>
                {loading ? <Skeleton width={120} /> : subscription.renewalDate}
              </Typography>
            </CardContent>
          </PagePanel>
        </Grid>
      </Grid>

      <PagePanel tone="neutral">
        <CardContent>
          <Typography sx={{ ...pageTypography.panelTitle, mb: 1 }}>
            Abbonamento attuale
          </Typography>
          <Typography sx={{ ...pageTypography.bodyText, mb: 2 }}>
            Importo mensile: {subscription.amount} {subscription.currency}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button variant="contained" startIcon={<CreditCardIcon />} onClick={checkout} disabled={redirecting}>
              Vai al checkout Stripe
            </Button>
            <Button variant="outlined" onClick={loadBilling}>
              Aggiorna stato
            </Button>
          </Stack>
        </CardContent>
      </PagePanel>
    </PageShell>
  );
}
